import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { GiSchoolBag, GiNotebook } from "react-icons/gi";
import { RiBookShelfFill, RiSchoolLine } from "react-icons/ri";
import { TfiWrite } from "react-icons/tfi";
import { IoSchool } from "react-icons/io5";

const backgroundImages = [
  "/renamed_website_images/school-library-mobile.jpg",
  "/renamed_website_images/library-reading-1.jpg",
  "/renamed_website_images/library-reading-2.jpg",
];

const features = [
  {
    icon: <RiBookShelfFill size={22} />,
    text: "Wide Collection of Books",
    img: "/renamed_website_images/library-reading-1.jpg",
    color: "#f4c842",
    border: "rgba(244,200,66,0.25)",
  },
  {
    icon: <GiSchoolBag size={22} />,
    text: "Dedicated Reading Area",
    img: "/renamed_website_images/library-reading-2.jpg",
    color: "#6366f1",
    border: "rgba(99,102,241,0.25)",
  },
  {
    icon: <IoSchool size={22} />,
    text: "Weekly Library Periods",
    img: "/renamed_website_images/library-reading-3.jpg",
    color: "#22c55e",
    border: "rgba(34,197,94,0.25)",
  },
  {
    icon: <TfiWrite size={20} />,
    text: "Interactive Storytelling Sessions",
    img: "/renamed_website_images/library-001.jpg",
    color: "#f97316",
    border: "rgba(249,115,22,0.25)",
  },
  {
    icon: <GiNotebook size={22} />,
    text: "Library Club Activities",
    img: "/renamed_website_images/library-002.jpg",
    color: "#8b5cf6",
    border: "rgba(139,92,246,0.25)",
  },
  {
    icon: <RiSchoolLine size={22} />,
    text: "Multilingual Book Collection",
    img: "/renamed_website_images/library-003.jpg",
    color: "#06b6d4",
    border: "rgba(6,182,212,0.25)",
  },
];

const SchoolLibrary = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length,
      );
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden bg-[#020810]">
      {/* Smooth Cross-fade Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {backgroundImages.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url('${img}')`,
              opacity: currentImageIndex === idx ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
            }}
          />
        ))}
      </div>

      {/* Dark overlay */}
      <div
        className="absolute inset-0 z-1"
        style={{ background: "rgba(2,8,16,0.88)" }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <Link
          to="/facilities"
          className="inline-block text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          School Library
        </h1>
        <div className="cosmic-divider mb-10" />

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {features.map((item, i) => (
            <div
              key={i}
              className="glass-card p-5 flex items-center gap-5 transition-all duration-300 hover:scale-[1.01]"
              style={{ borderColor: item.border }}
            >
              <img
                src={item.img}
                alt={item.text}
                className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
                style={{ border: `1px solid ${item.border}` }}
              />
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${item.color}18`,
                    border: `1px solid ${item.border}`,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </div>
                <p
                  className="font-semibold"
                  style={{
                    fontFamily: "Cinzel, serif",
                    color: "#f0f5ff",
                    fontSize: "0.95rem",
                  }}
                >
                  {item.text}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-white/10">
          <Link
            to="/facilities/smart-class"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: Smart Classes
          </Link>
          <Link
            to="/facilities/music-room"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: Music Room <FaArrowRight />
          </Link>
        </div>
      </div>

      {/* Side floating arrows */}
      <Link
        to="/facilities/smart-class"
        aria-label="Previous: Smart Classes"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/music-room"
        aria-label="Next: Music Room"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default SchoolLibrary;
